import { useQuery } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '~/components/Pagination';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { ArtistSearchBar } from '~/modules/artist-roster/components/ArtistSearchBar/ArtistSearchBar';
import { useArtistQuery } from '~/modules/artist-roster/hooks/useArtistQuery';
import type { RosterFilterUrlParams } from '~/modules/artist-roster/hooks/useRosterUrlParams/types';
import type { ArtistUrlParamsHook } from '~/modules/artist-roster/hooks/useUrlParams/types';
import { getLabels } from '~/modules/artist-roster/lib/labels';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import { type LabelData } from '~/modules/artist-roster/lib/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { ArtistExport } from '../ArtistExport/ArtistExport';
import { ArtistAppliedFilterList } from '../ArtistFilter/ArtistAppliedFilter/ArtistAppliedFilterList';
import { DEFAULT_ARTIST_FILTERS } from '../ArtistFilter/constants';
import type { RosterFilters } from '../ArtistFilter/types';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { FilterButton } from '../Filtering/FilterButton';
import type { FiltersMetadata } from '../Filtering/types';
import type { ArtistAppliedLabelFilter } from './../ArtistFilter/ArtistAppliedFilter/ArtistAppliedFilterList';

interface RosterHeaderProps {
  rosterUrlParamsProps: ArtistUrlParamsHook<RosterFilterUrlParams>;
  filteredLabels: LabelData[];
  setFilteredLabels: (labels: LabelData[]) => void;
  setIsFilterPanelOpen: Dispatch<SetStateAction<boolean>>;
}

export function RosterHeader({
  rosterUrlParamsProps,
  filteredLabels,
  setFilteredLabels,
  setIsFilterPanelOpen,
}: RosterHeaderProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const ref = useRef<HTMLDivElement>(null);
  const { filters, pageNumber, setFilters, updateFilters, setPageNumber } =
    rosterUrlParamsProps;
  const { labelIds, searchText, isCrossover, isOnRoster } = filters;
  const { headerRef, getInterpolatedValue } = useScrollCollapseContext();

  // This is the list of applied filter objects displayed under the search bar.
  const appliedFilterItems = useMemo<ArtistAppliedLabelFilter[]>(() => {
    return filteredLabels.map((label) => {
      const { labelId, name, updatedAt, updatedBy } = label;

      return {
        labelId,
        displayName: name,
        updatedAt,
        updatedBy,
      };
    });
  }, [filteredLabels]);

  const filtersMetadata: FiltersMetadata = useMemo(() => {
    return {
      areFiltersApplied:
        filteredLabels.length > 0 || isCrossover || isOnRoster !== undefined,
      filterCount:
        filteredLabels.length +
        (isCrossover ? 1 : 0) +
        (isOnRoster !== undefined ? 1 : 0),
    };
  }, [filteredLabels, isCrossover, isOnRoster]);

  const { data: artistsData, isLoading: isLoadingArtists } = useArtistQuery({
    searchText,
    labelIds,
    isCrossover,
    isOnRoster,
    pageNumber,
  });

  useEffect(() => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [pageNumber]);

  const totalArtists = artistsData?.pagination.total ?? 0;

  const { data: labelData } = useQuery({
    queryKey: ['labels'],
    queryFn: () => getLabels({}),
  });

  // Update the filtered label data once the data loads.
  useEffect(() => {
    if (labelIds && labelData) {
      const filteredData = labelData.filter((label: LabelData) =>
        labelIds.includes(label.labelId),
      );

      setFilteredLabels(filteredData);
    }
  }, [labelData, labelIds, setFilteredLabels]);

  const handleSearchInputClear = useCallback(() => {
    // NOTE: Use undefined here to specifically clear the search text from the URL.
    updateFilters({ searchText: undefined });
  }, [updateFilters]);

  const handleSubmitSearch = useCallback(
    (searchValue: string) => {
      trackEvent('rosterArtistSearch', {
        roster_label_filters_count: labelIds.length,
        roster_label_filters_applied: labelIds.length > 0,
        roster_crossover_filter_applied: isCrossover,
        roster_status: getRosterStatus(isOnRoster),
        search_term: searchValue,
      });

      // Make sure to clear the search if it's an empty string. We clear by
      // passing undefined to the updateFilters function.
      updateFilters({ searchText: searchValue || undefined });
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent, updateFilters],
  );

  const handleRemoveLabelFilter = useCallback(
    (deletedLabelId: number) => {
      const newFilteredLabelData = filteredLabels.filter(
        ({ labelId }) => labelId !== deletedLabelId,
      );

      setFilteredLabels(newFilteredLabelData);

      updateFilters({
        labelIds: newFilteredLabelData.map((label) => label.labelId),
        isCrossover,
      });
    },
    [filteredLabels, isCrossover, updateFilters, setFilteredLabels],
  );

  const handleRemoveCrossoverFilter = useCallback(() => {
    updateFilters({
      isCrossover: false,
    });
  }, [updateFilters]);

  const handleRemoveOnRosterFilter = useCallback(() => {
    updateFilters({
      isOnRoster: undefined,
    });
  }, [updateFilters]);

  const trackPaginationEvent = useCallback(
    (directionToNextPage: boolean) => {
      trackEvent('rosterTablePaginationClick', {
        roster_label_filters_count: labelIds.length,
        roster_crossover_filter_applied: isCrossover,
        roster_label_filters_applied: labelIds.length > 0,
        roster_status: getRosterStatus(isOnRoster),
        direction: directionToNextPage ? 'next_page' : 'previous_page',
      });
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent],
  );

  const handleApplyFilters = useCallback(
    (appliedFilters: RosterFilters) => {
      setFilteredLabels(appliedFilters.labels);
      setFilters({
        isCrossover: appliedFilters.isCrossover,
        isOnRoster: appliedFilters.isOnRoster,
        labelIds: appliedFilters.labels.map((label) => label.labelId),
        searchText,
      });

      trackEvent('rosterLabelFiltersApplied', {
        roster_label_filters_count: appliedFilters.labels.length,
        roster_crossover_filter_applied: appliedFilters.isCrossover,
        roster_label_filters_applied: appliedFilters.labels.length > 0,
        roster_status: getRosterStatus(appliedFilters.isOnRoster),
      });
    },
    [searchText, setFilters, trackEvent, setFilteredLabels],
  );

  const handleClearFilters = useCallback(() => {
    handleApplyFilters(DEFAULT_ARTIST_FILTERS);
  }, [handleApplyFilters]);

  const handleShowFilters = useCallback(
    (isOpen: boolean) => {
      setIsFilterPanelOpen(isOpen);

      if (isOpen) {
        trackEvent('rosterFiltersClick', {
          roster_label_filters_count: labelIds.length,
          roster_crossover_filter_applied: isCrossover,
          roster_label_filters_applied: labelIds.length > 0,
          roster_status: getRosterStatus(isOnRoster),
        });
      }
    },
    [
      isCrossover,
      labelIds.length,
      isOnRoster,
      trackEvent,
      setIsFilterPanelOpen,
    ],
  );

  const handleAppliedFiltersMoreClick = useCallback(() => {
    setIsFilterPanelOpen((isOpen) => !isOpen);
  }, [setIsFilterPanelOpen]);

  return (
    <div
      className="sticky top-0 z-20 bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat px-xl pt-lg bg-level-01/full"
      ref={headerRef}
    >
      <div className="mb-lg flex items-end justify-between">
        <h1
          className="text-display-md font-bold duration-150 will-change-[font-size]"
          // Transition from "text-display-md" to "text-headline-sm".
          style={{ fontSize: getInterpolatedValue(32, 18) }}
        >
          {t('header.heading')}
        </h1>
        {!isLoadingArtists && !!totalArtists ? (
          <ArtistExport
            search={searchText}
            isCrossover={isCrossover}
            isOnRoster={isOnRoster}
            labels={labelIds}
          />
        ) : null}
      </div>
      <div className="mb-lg flex gap-md" role="search">
        <ArtistSearchBar
          initialSearchText={searchText ?? ''}
          placeholder={t('filters.searchForArtist')}
          onSubmit={handleSubmitSearch}
          onClear={handleSearchInputClear}
        />
        <FilterButton
          filtersMetadata={filtersMetadata}
          onShowFilters={handleShowFilters}
        />
      </div>
      {isCrossover || isOnRoster !== undefined || labelIds.length > 0 ? (
        <ArtistAppliedFilterList
          isCrossover={isCrossover}
          isOnRoster={isOnRoster}
          labelFilters={appliedFilterItems}
          onRemoveCrossover={handleRemoveCrossoverFilter}
          onRemoveOnRoster={handleRemoveOnRosterFilter}
          onRemoveLabel={handleRemoveLabelFilter}
          onClear={handleClearFilters}
          onMoreClick={handleAppliedFiltersMoreClick}
        />
      ) : null}
      <div className="mb-md inline-flex min-h-xl w-full items-center justify-between">
        <div className="flex gap-xs">
          <h2>{t('header.subheading')}</h2>
          {!isLoadingArtists ? (
            <div className="text-subtle">({totalArtists.toLocaleString()})</div>
          ) : null}
        </div>
        {totalArtists > DEFAULT_PAGE_SIZE ? (
          <Pagination
            page={pageNumber}
            total={totalArtists}
            perPage={DEFAULT_PAGE_SIZE}
            setPageNumber={(pageNum) => {
              trackPaginationEvent(pageNum > pageNumber);
              setPageNumber(pageNum);
            }}
            showAsLink={true}
          />
        ) : null}
      </div>
    </div>
  );
}

RosterHeader.displayName = 'RosterHeader';
